// In App.tsx
import { BrowserRouter as Router, Routes, Route, useNavigate, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider, useAuth } from './context/AuthContext';
import { BanksProvider } from './context/BanksContext';
import Layout from './components/Layout';
import NotificationHost from './components/NotificationHost';
import HomePage from './pages/HomePage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import DashboardPage from './pages/DashboardPage';
import NewsPage from './pages/NewsPage';
import NewsArticlePage from './pages/NewsArticlePage';
import ContactPage from './pages/ContactPage';
import ContatoPage from './pages/ContatoPage';
import ManualsPage from './pages/ManualsPage';
import DownloadsPage from './pages/DownloadsPage';
import LegislationPage from './pages/LegislationPage';
import ConveniosPage from './pages/ConveniosPage';
import EmendaDetailPage from './pages/EmendaDetailPage';
import CouncilorsPage from './pages/CouncilorsPage';
import CadastroDadosInstitucionaisPage from './pages/CadastroDadosInstitucionaisPage';
import CadastroDirigentePage from './pages/CadastroDirigentePage';
import CadastroEmendaPage from './pages/CadastroEmendaPage';
import PreProjetosPage from './pages/PreProjetosPage';
import PreProjetoDetailPage from './pages/PreProjetoDetailPage';
import GestaoPreProjetosPage from './pages/GestaoPreProjetosPage';
import ProtectedRoute from './components/ProtectedRoute';
import DashboardLayout from './components/DashboardLayout';
import DashboardHomePage from './pages/DashboardHomePage';
import DashboardEmendasPage from './pages/DashboardEmendasPage';
import EmendasVisaoGeralPage from './pages/EmendasVisaoGeralPage';
import DashboardInstitutionsPage from './pages/DashboardInstitutionsPage';
import GestorJuridicoDashboardPage from './pages/GestorJuridicoDashboardPage';
import UsersPage from './pages/UsersPage';
import ProfilePage from './pages/ProfilePage';
import SolicitacoesPage from './pages/SolicitacoesPage';
import SolicitacaoDetalhePage from './pages/SolicitacaoDetalhePage';
import DiretoriaPage from './pages/DiretoriaPage';
import VerifyEmailPage from './pages/VerifyEmailPage';
import ForgotPasswordPage from './pages/ForgotPasswordPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import TiposDocumentoConfigPage from './pages/TiposDocumentoConfigPage';
import PlanoFullPage from './pages/PlanoFullPage';
import PlanoAnaliseJuridicaPage from './pages/PlanoAnaliseJuridicaPage';
import PlanoAnaliseJuridicaPrintPage from './pages/PlanoAnaliseJuridicaPrintPage';
import ParecerGestorPrintPage from './pages/ParecerGestorPrintPage';
import PlanoTrabalhoPrintPage from './pages/PlanoTrabalhoPrintPage';
import PrestacaoContasPage from './pages/PrestacaoContasPage';
import PrestacaoContasRelatoriosPage from './pages/PrestacaoContasRelatoriosPage';
import PlanoTrabalhoPage from './pages/PlanoTrabalhoPage';
import AdminParlamentarLimitesPage from './pages/AdminParlamentarLimitesPage';
import LeisOrcamentariasPage from './pages/LeisOrcamentariasPage';
import FuncionalidadesConfigPage from './pages/FuncionalidadesConfigPage';
import WorkflowPage from './pages/WorkflowPage';
import PlanosGestaoPage from './pages/PlanosGestaoPage';
import PrestacoesPlanosGestaoPage from './pages/PrestacoesPlanosGestaoPage';
import ReportsEmendasPage from './pages/ReportsEmendasPage';
import ReportsInstituicoesPage from './pages/ReportsInstituicoesPage';
import ReportsPreProjetosPage from './pages/ReportsPreProjetosPage';
import RelatorioPendenciasPage from './pages/RelatorioPendenciasPage';
import ComissaoAdminPage from './pages/ComissaoAdminPage';
import PermissoesConfigPage from './pages/PermissoesConfigPage';
import AdminEmailOutboxPage from './pages/AdminEmailOutboxPage';
import ParametrosSistemaPage from './pages/ParametrosSistemaPage';
import InstitutionCompliancePrintPage from './pages/InstitutionCompliancePrintPage';
import ProcuracoesPage from './pages/ProcuracoesPage';
import { Permission } from './types/user.types';
import React from 'react';
import TransparenciaPublicaPage from './pages/TransparenciaPublicaPage';
import TransparenciaEmendaPage from './pages/TransparenciaEmendaPage';
import TransparenciaParlamentaresPage from './pages/TransparenciaParlamentaresPage';
import TransparenciaParlamentarPage from './pages/TransparenciaParlamentarPage';
import ExecucaoFinanceiraPage from './pages/ExecucaoFinanceiraPage';
import ExecucaoFinanceiraDetailPage from './pages/ExecucaoFinanceiraDetailPage';
import TaepAvaliacaoPage from './pages/TaepAvaliacaoPage';

// Create a client
const queryClient = new QueryClient();

const ReportsRedirect: React.FC = () => {
  const { hasPermission } = useAuth();

  if (hasPermission(Permission.VIEW_EMENDA_REPORTS)) {
    return <Navigate to="/dashboard/relatorios/emendas" replace />;
  }

  if (hasPermission(Permission.VIEW_INSTITUTION_REPORTS)) {
    return <Navigate to="/dashboard/relatorios/instituicoes" replace />;
  }

  if (hasPermission(Permission.VIEW_PRE_PROJETO_REPORTS)) {
    return <Navigate to="/dashboard/relatorios/pre-projetos" replace />;
  }

  return <Navigate to="/dashboard" replace />;
};

const PlanosGestaoRoute: React.FC = () => {
  const { user } = useAuth();

  if (String(user?.role ?? '').toUpperCase() === 'OPERADOR') {
    return <Navigate to="/dashboard/planos" replace />;
  }

  return (
    <ProtectedRoute requiredPermissions={[Permission.VIEW_GESTAO_PLANOS]}>
      <PlanosGestaoPage />
    </ProtectedRoute>
  );
};

function App() {
  // Use /emendas as basename only when the URL actually starts with /emendas
  // (i.e., behind the Apache proxy on the production server).
  // Direct Docker access (IP:3005) and localhost both serve from /, so basename = '/'.
  const routerBasename = window.location.pathname.startsWith('/emendas') ? '/emendas' : '/';

  // Runtime import/type checks to help debug "Element type is invalid" errors.
  // This logs the typeof each imported page/component and flags items that look like module objects
  // (i.e., { default: ... }) instead of a function/component.
  React.useEffect(() => {
    try {
      const checks: Array<{ name: string; comp: any }> = [
        { name: 'Layout', comp: Layout },
        { name: 'HomePage', comp: HomePage },
        { name: 'LoginPage', comp: LoginPage },
        { name: 'RegisterPage', comp: RegisterPage },
        { name: 'DashboardPage', comp: DashboardPage },
        { name: 'NewsPage', comp: NewsPage },
        { name: 'NewsArticlePage', comp: NewsArticlePage },
        { name: 'ContactPage', comp: ContactPage },
        { name: 'ManualsPage', comp: ManualsPage },
        { name: 'DownloadsPage', comp: DownloadsPage },
        { name: 'LegislationPage', comp: LegislationPage },
        { name: 'ConveniosPage', comp: ConveniosPage },
        { name: 'EmendaDetailPage', comp: EmendaDetailPage },
        { name: 'CouncilorsPage', comp: CouncilorsPage },
        { name: 'CadastroDadosInstitucionaisPage', comp: CadastroDadosInstitucionaisPage },
        { name: 'CadastroDirigentePage', comp: CadastroDirigentePage },
        { name: 'CadastroEmendaPage', comp: CadastroEmendaPage },
        { name: 'ProtectedRoute', comp: ProtectedRoute },
        { name: 'DashboardLayout', comp: DashboardLayout },
        { name: 'DashboardHomePage', comp: DashboardHomePage },
        { name: 'DashboardEmendasPage', comp: DashboardEmendasPage },
        { name: 'UsersPage', comp: UsersPage },
        { name: 'ProfilePage', comp: ProfilePage },
        { name: 'DiretoriaPage', comp: DiretoriaPage },
        { name: 'VerifyEmailPage', comp: VerifyEmailPage },
        { name: 'ForgotPasswordPage', comp: ForgotPasswordPage },
        { name: 'ResetPasswordPage', comp: ResetPasswordPage },
        { name: 'TiposDocumentoConfigPage', comp: TiposDocumentoConfigPage },
        { name: 'PlanoFullPage', comp: PlanoFullPage },
        { name: 'PrestacaoContasPage', comp: PrestacaoContasPage },
        { name: 'PlanoTrabalhoPage', comp: PlanoTrabalhoPage },
        { name: 'AdminParlamentarLimitesPage', comp: AdminParlamentarLimitesPage },
        { name: 'FuncionalidadesConfigPage', comp: FuncionalidadesConfigPage },
      ];

    } catch (err) {
      console.error('Import check failed', err);
    }
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <Router basename={routerBasename}>
        <NotificationHost />
        <AuthProvider>
          <BanksProvider>
           <Routes>
              {/* Transparencia publica (sem login) */}
              <Route path="/transparencia" element={<TransparenciaPublicaPage />} />
              <Route path="/transparencia/emendas/:officialCode" element={<TransparenciaEmendaPage />} />
              <Route path="/transparencia/parlamentares" element={<TransparenciaParlamentaresPage />} />
              <Route path="/transparencia/parlamentares/:slug" element={<TransparenciaParlamentarPage />} />

              {/* Email verification (public route) */}
              <Route path="/verify-email" element={<VerifyEmailPage />} />

              {/* Password reset (public routes) */}
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/reset-password" element={<ResetPasswordPage />} />

              {/* Dashboard SPA layout (task-6) */}
              <Route
                path="/dashboard"
                element={
                  <ProtectedRoute>
                    <DashboardLayout />
                  </ProtectedRoute>
                }
              >
              {/* default content */}
              <Route index element={<DashboardHomePage />} />
              <Route path="gestor-juridico" element={
                <ProtectedRoute requiredRoles={['ADMIN', 'GESTOR_JURIDICO']}>
                  <GestorJuridicoDashboardPage />
                </ProtectedRoute>
              } />
              <Route path="plano/full/:id" element={
                <ProtectedRoute requiredPermissions={[Permission.VIEW_GESTAO_PLANOS, Permission.CREATE_PLANO_TRABALHO, Permission.ACCESS_FILA_JURIDICA]}>
                  <PlanoFullPage />
                </ProtectedRoute>
              } />
              <Route path="plano/:id/analise-juridica" element={
                <ProtectedRoute requiredRoles={['ADMIN', 'JURIDICO', 'JURIDICO_AUXILIAR', 'GESTOR_JURIDICO']}>
                  <PlanoAnaliseJuridicaPage />
                </ProtectedRoute>
              } />
              <Route path="plano/prestacao-contas" element={
                <ProtectedRoute requiredPermissions={[Permission.VIEW_PRESTACAO_CONTAS]}>
                  <PrestacaoContasPage />
                </ProtectedRoute>
              } />
              <Route path="prestacao-contas/relatorios" element={
                <ProtectedRoute requiredPermissions={[Permission.VIEW_PRESTACAO_CONTAS]}>
                  <PrestacaoContasRelatoriosPage />
                </ProtectedRoute>
              } />
              <Route path="plano-trabalho" element={<ProtectedRoute requiredPermissions={[Permission.VIEW_GESTAO_PLANOS]}><PlanoTrabalhoPage /></ProtectedRoute>} />
              <Route path="planos" element={<ProtectedRoute requiredPermissions={[Permission.VIEW_GESTAO_PLANOS, Permission.CREATE_PLANO_TRABALHO]}><PlanoTrabalhoPage /></ProtectedRoute>} />
              <Route path="gestao/planos-trabalho" element={
                <PlanosGestaoRoute />
              } />
              <Route path="gestao/prestacao-contas-planos" element={
                <ProtectedRoute requiredPermissions={[Permission.VIEW_PRESTACAO_CONTAS]}>
                  <PrestacoesPlanosGestaoPage />
                </ProtectedRoute>
              } />
              <Route path="leis-orcamentarias" element={
                <ProtectedRoute requiredPermissions={[Permission.MANAGE_LEIS_ORCAMENTARIAS]}>
                  <LeisOrcamentariasPage />
                </ProtectedRoute>
              } />
              <Route path="taep" element={
                <ProtectedRoute requiredPermissions={[Permission.MANAGE_TAEP]}>
                  <TaepAvaliacaoPage />
                </ProtectedRoute>
              } />
              <Route path="relatorios" element={<ReportsRedirect />} />
              <Route path="relatorios/emendas" element={
                <ProtectedRoute requiredPermissions={[Permission.VIEW_EMENDA_REPORTS]}>
                  <ReportsEmendasPage />
                </ProtectedRoute>
              } />
              <Route path="relatorios/instituicoes" element={
                <ProtectedRoute requiredPermissions={[Permission.VIEW_INSTITUTION_REPORTS]}>
                  <ReportsInstituicoesPage />
                </ProtectedRoute>
              } />
              <Route path="relatorios/pre-projetos" element={
                <ProtectedRoute requiredPermissions={[Permission.VIEW_PRE_PROJETO_REPORTS]}>
                  <ReportsPreProjetosPage />
                </ProtectedRoute>
              } />
              <Route path="relatorios/pendencias" element={
                <Navigate to="/dashboard/relatorios/pendencias/gestores" replace />
              } />
              <Route path="relatorios/pendencias/:tab" element={
                <ProtectedRoute requiredPermissions={[Permission.VIEW_PENDENCIAS_REPORTS]}>
                  <RelatorioPendenciasPage />
                </ProtectedRoute>
              } />
              <Route
                path="relatorios/secretarias-sem-plano"
                element={<Navigate to="/dashboard/relatorios/pendencias/secretarias-sem-plano" replace />}
              />
              <Route path="workflow" element={
                <ProtectedRoute requiredRoles={['ADMIN']}>
                  <WorkflowPage />
                </ProtectedRoute>
              } />
              <Route path="novo-plano" element={
                <ProtectedRoute requiredPermissions={[Permission.CREATE_PLANO_TRABALHO]}>
                  <PlanoFullPage />
                </ProtectedRoute>
              } />
              <Route path="editar-plano" element={
                <ProtectedRoute requiredPermissions={[Permission.VIEW_GESTAO_PLANOS, Permission.CREATE_PLANO_TRABALHO, Permission.ACCESS_FILA_JURIDICA]}>
                  <PlanoFullPage />
                </ProtectedRoute>
              } />
              <Route path="emendas" element={<DashboardEmendasPage />} />
              <Route path="solicitacoes" element={
                <ProtectedRoute requiredPermissions={[Permission.CREATE_SOLICITACAO_ALTERACAO, Permission.TRATAR_SOLICITACAO_ALTERACAO]}>
                  <SolicitacoesPage />
                </ProtectedRoute>
              } />
              <Route path="solicitacoes/:id" element={
                <ProtectedRoute requiredPermissions={[Permission.CREATE_SOLICITACAO_ALTERACAO, Permission.TRATAR_SOLICITACAO_ALTERACAO]}>
                  <SolicitacaoDetalhePage />
                </ProtectedRoute>
              } />
              <Route path="execucao-financeira" element={
                <ProtectedRoute requiredPermissions={[Permission.VIEW_EXECUCAO_FINANCEIRA]}>
                  <ExecucaoFinanceiraPage />
                </ProtectedRoute>
              } />
              <Route path="execucao-financeira/:emendaId" element={
                <ProtectedRoute requiredPermissions={[Permission.VIEW_EXECUCAO_FINANCEIRA]}>
                  <ExecucaoFinanceiraDetailPage />
                </ProtectedRoute>
              } />
              <Route path="emendas/visao-geral" element={<ProtectedRoute><EmendasVisaoGeralPage /></ProtectedRoute>} />
              <Route path="emendas/:id" element={<ProtectedRoute requiredPermissions={[Permission.VIEW_EMENDAS]}><EmendaDetailPage /></ProtectedRoute>} />
              <Route path="cadastro-emenda" element={<ProtectedRoute requiredPermissions={[Permission.CREATE_EMENDA]}><CadastroEmendaPage /></ProtectedRoute>} />
              <Route path="pre-projetos" element={<ProtectedRoute requiredPermissions={[Permission.VIEW_PRE_PROJETOS]}><PreProjetosPage /></ProtectedRoute>} />
              <Route path="pre-projetos/novo" element={<ProtectedRoute requiredPermissions={[Permission.MANAGE_PRE_PROJETOS]}><PreProjetoDetailPage /></ProtectedRoute>} />
              <Route path="pre-projetos/:id" element={<ProtectedRoute requiredPermissions={[Permission.VIEW_PRE_PROJETOS]}><PreProjetoDetailPage /></ProtectedRoute>} />
              <Route path="gestao-pre-projetos" element={<ProtectedRoute requiredPermissions={[Permission.VIEW_PRE_PROJETOS]}><GestaoPreProjetosPage /></ProtectedRoute>} />
              <Route path="instituicoes" element={
                <ProtectedRoute>
                  <DashboardInstitutionsPage />
                </ProtectedRoute>
              } />
              <Route path="procuracoes" element={
                <ProtectedRoute requiredRoles={['OPERADOR', 'ADMIN']}>
                  <ProcuracoesPage />
                </ProtectedRoute>
              } />
              <Route path="cadastro-dados-institucionais" element={
                <ProtectedRoute>
                  <CadastroDadosInstitucionaisPage />
                </ProtectedRoute>
              } />
              <Route path="cadastro-dirigente" element={
                <ProtectedRoute>
                  <CadastroDirigentePage />
                </ProtectedRoute>
              } />
                <Route path="diretoria" element={
                  <ProtectedRoute>
                    <DiretoriaPage />
                  </ProtectedRoute>
                } />
                <Route path="parlamentares" element={
                  <ProtectedRoute>
                    <CouncilorsPage />
                  </ProtectedRoute>
                } />
                <Route path="usuarios" element={
                  <ProtectedRoute requiredRoles={['ADMIN']}>
                    <UsersPage />
                  </ProtectedRoute>
                } />
                <Route path="profile" element={<ProfilePage />} />
                <Route path="tipos-documento-config" element={
                  <ProtectedRoute requiredPermissions={[Permission.MANAGE_TIPOS_DOCUMENTO]}>
                    <TiposDocumentoConfigPage />
                  </ProtectedRoute>
                } />
                <Route path="admin/funcionalidades" element={
                  <ProtectedRoute requiredRoles={['ADMIN']}>
                    <FuncionalidadesConfigPage />
                  </ProtectedRoute>
                } />
                <Route path="admin/parlamentar-limites" element={
                  <ProtectedRoute requiredPermissions={[Permission.MANAGE_PARLAMENTAR_LIMITES, Permission.VIEW_PARLAMENTAR_LIMITES]}>
                    <AdminParlamentarLimitesPage />
                  </ProtectedRoute>
                } />
                <Route path="admin/comissoes" element={
                  <ProtectedRoute requiredPermissions={[Permission.MANAGE_COMISSOES]}>
                    <ComissaoAdminPage />
                  </ProtectedRoute>
                } />
                <Route path="admin/permissoes" element={
                  <ProtectedRoute requiredRoles={['ADMIN']}>
                    <PermissoesConfigPage />
                  </ProtectedRoute>
                } />
                <Route path="admin/emails" element={
                  <ProtectedRoute requiredRoles={['ADMIN']}>
                    <AdminEmailOutboxPage />
                  </ProtectedRoute>
                } />
                <Route path="admin/parametros" element={
                  <ProtectedRoute requiredRoles={['ADMIN']}>
                    <ParametrosSistemaPage />
                  </ProtectedRoute>
                } />
                <Route path="camara/area-trabalho" element={<Navigate to="/dashboard" replace />} />
                <Route path="*" element={<Navigate to="/dashboard" replace />} />
              </Route>

              {/* Full-screen pages (no header/footer) */}
              {/* /emendas e /emendas/:id não têm rota aqui de propósito — a versão
                  mantida vive em /dashboard/emendas[/:id] (permission-gated via
                  ROUTE_PERMISSIONS), e o redirect de rota legada logo abaixo cobre
                  quem ainda usa a URL antiga. Servir a página antiga aqui deixava as
                  duas rotas com regra de acesso divergente uma da outra. */}
              <Route path="/plano/full/:id" element={
                <ProtectedRoute requiredPermissions={[Permission.VIEW_GESTAO_PLANOS, Permission.CREATE_PLANO_TRABALHO, Permission.ACCESS_FILA_JURIDICA]}>
                  <PlanoFullPage />
                </ProtectedRoute>
              } />
              <Route path="/plano/impressao/:id" element={
                <ProtectedRoute requiredPermissions={[Permission.VIEW_GESTAO_PLANOS, Permission.CREATE_PLANO_TRABALHO]}>
                  <PlanoTrabalhoPrintPage />
                </ProtectedRoute>
              } />
              <Route path="/plano/analise-juridica/impressao/:id" element={
                <ProtectedRoute requiredRoles={['ADMIN', 'JURIDICO', 'JURIDICO_AUXILIAR', 'GESTOR_JURIDICO']}>
                  <PlanoAnaliseJuridicaPrintPage />
                </ProtectedRoute>
              } />
              <Route path="/plano/parecer-gestor/impressao/:id" element={
                <ProtectedRoute requiredRoles={['ADMIN', 'SECRETARIA']}>
                  <ParecerGestorPrintPage />
                </ProtectedRoute>
              } />
              <Route path="/instituicao/impressao/:id" element={
                <ProtectedRoute requiredPermissions={[Permission.VIEW_INSTITUTIONS]}>
                  <InstitutionCompliancePrintPage />
                </ProtectedRoute>
              } />
              <Route path="/plano/prestacao-contas" element={
                <ProtectedRoute requiredPermissions={[Permission.VIEW_PRESTACAO_CONTAS]}>
                  <PrestacaoContasPage />
                </ProtectedRoute>
              } />

              {/* Redirect legacy routes to dashboard */}
              <Route path="/emendas" element={<Navigate to="/dashboard/emendas" replace />} />
              <Route path="/emendas/:id" element={<Navigate to="/dashboard/emendas" replace />} />
              <Route
                path="/painel/emendas"
                element={<Navigate to="/dashboard/emendas" replace />}
              />

              {/* Standalone landing page — no navbar/footer */}
              <Route path="/" element={<HomePage />} />

              {/* Standalone public pages — no navbar/footer */}
              <Route path="/login" element={<LoginPage />} />
              <Route path="/register" element={<RegisterPage />} />
              <Route path="/contato" element={<ContatoPage />} />

              {/* All other routes rendered inside Layout */}
              <Route path="/*" element={<Layout />}>
                <Route path="login" element={<LoginPage />} />
                <Route path="register" element={<RegisterPage />} />

                {/* Backward compatible redirect: /painel -> /dashboard */}
                <Route path="painel" element={<Navigate to="/dashboard" replace />} />

                 <Route
                  path="painel-old"
                  element={
                    <ProtectedRoute>
                      <DashboardPage />
                    </ProtectedRoute>
                  }
                />
                 <Route path="news" element={<NewsPage />} />
                 <Route path="news/:id" element={<NewsArticlePage />} />
                 <Route path="contact" element={<ContactPage />} />
                 <Route path="manuals" element={<ManualsPage />} />
                 <Route path="downloads" element={<DownloadsPage />} />
                 <Route path="legislation" element={<LegislationPage />} />
                 <Route
                   path="painel/convenios"
                   element={
                     <ProtectedRoute>
                       <ConveniosPage />
                     </ProtectedRoute>
                   }
                 />
                 <Route
                   path="painel/councilors"
                   element={
                     <ProtectedRoute>
                       <CouncilorsPage />
                     </ProtectedRoute>
                   }
                 />
               </Route>
             </Routes>
          </BanksProvider>
        </AuthProvider>
       </Router>
     </QueryClientProvider>
   );
 }

 export default App;
